import liveServerStatus from '../../liveServerStatus';

/**
 * Generates HTML for task status update, archive, or delete notification emails
 * @param {Object} taskData - The task information
 * @param {Object} updaterData - Information about person who updated the task
 * @param {String} actionType - Type of action: 'status-update', 'archive', or 'delete'
 * @param {String} previousStatus - Previous status (for status updates)
 * @param {String} newStatus - New status (for status updates)
 * @returns {String} HTML email content
 */
const generateTaskStatusUpdateEmailHTML = (taskData, updaterData, actionType, previousStatus = '', newStatus = '') => {
  const taskTitle = taskData?.title || 'Untitled Task';
  const updaterName = updaterData?.name || updaterData?.email || 'A team member';
  const priorityColor = getPriorityColor(taskData?.priority);
  
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  
  // Determine action-specific content
  let actionTitle = '';
  let actionDescription = '';
  let headerColor = '#51796F'; // Default green color
  
  switch(actionType) {
    case 'status-update':
      actionTitle = 'Task Status Updated';
      actionDescription = `<strong>${updaterName}</strong> has updated the status of this task from <strong>${previousStatus}</strong> to <strong>${newStatus}</strong>.`;
      headerColor = '#2196f3'; // Blue
      break;
    case 'archive':
      actionTitle = 'Task Archived';
      actionDescription = `<strong>${updaterName}</strong> has archived this task. Archived tasks are no longer active but remain accessible for reference.`;
      headerColor = '#9e9e9e'; // Gray
      break;
    case 'delete':
      actionTitle = 'Task Deleted';
      actionDescription = `<strong>${updaterName}</strong> has deleted this task. This notification serves as a record of the deletion.`;
      headerColor = '#f44336'; // Red
      break;
    default:
      actionTitle = 'Task Updated';
      actionDescription = `<strong>${updaterName}</strong> has updated this task.`;
  }
  
  // Add test environment indicator in the header if not in live environment
  const testEnvironmentBanner = !liveServerStatus ? `
    <div style="background-color: #ffeb3b; color: #333; text-align: center; padding: 5px; font-weight: bold; font-size: 12px;">
      ⚠️ TEST EMAIL - No action required ⚠️
    </div>
  ` : '';
  
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${actionTitle}: ${taskTitle}</title>
      <style>
        body {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          line-height: 1.6;
          color: #333;
          margin: 0;
          padding: 0;
          background-color: #f9fafb;
        }
        .email-container {
          max-width: 600px;
          margin: 0 auto;
          background-color: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
        }
        .email-header {
          background-color: ${headerColor};
          color: white;
          padding: 20px;
          text-align: center;
        }
        .email-body {
          padding: 20px;
        }
        .email-footer {
          background-color: #f3f4f6;
          padding: 15px 20px;
          text-align: center;
          font-size: 12px;
          color: #6b7280;
        }
        .task-container {
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          padding: 15px;
          margin-bottom: 20px;
          background-color: #f9fafb;
        }
        .task-title {
          font-size: 18px;
          font-weight: 600;
          margin-top: 0;
          margin-bottom: 10px;
          color: #1f2937;
        }
        .task-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-bottom: 15px;
        }
        .task-badge {
          display: inline-block;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
        }
        .action-container {
          background-color: #ffffff;
          border: 1px solid #e5e7eb;
          border-left: 4px solid ${headerColor};
          border-radius: 6px;
          padding: 15px;
          margin-bottom: 20px;
        }
        .action-header {
          display: flex;
          justify-content: space-between;
          margin-bottom: 10px;
        }
        .updater {
          font-weight: 600;
          color: ${headerColor};
        }
        .action-date {
          color: #6b7280;
          font-size: 14px;
        }
        .action-button {
          display: inline-block;
          background-color: ${headerColor};
          color: white;
          text-decoration: none;
          padding: 10px 20px;
          border-radius: 4px;
          font-weight: 500;
          margin-top: 20px;
        }
        .disclaimer {
          margin-top: 20px;
          font-size: 12px;
          color: #6b7280;
        }
      </style>
    </head>
    <body>
      ${testEnvironmentBanner}
      <div class="email-container">
        <div class="email-header">
          <h1 style="margin: 0; font-size: 24px;">${actionTitle}</h1>
        </div>
        
        <div class="email-body">
          <p>Hello,</p>
          
          <p>${actionDescription}</p>
          
          <div class="task-container">
            <h2 class="task-title">${taskTitle}</h2>
            
            <div class="task-meta">
              ${taskData.priority ? `
              <span class="task-badge" style="background-color: ${priorityColor.bg}; color: ${priorityColor.text};">
                ${taskData.priority} Priority
              </span>
              ` : ''}
              
              ${taskData.dueDate ? `
              <span class="task-badge" style="background-color: #f3f4f6; color: #4b5563;">
                Due: ${formatDate(taskData.dueDate)}
              </span>
              ` : ''}
            </div>
            
            ${taskData.description ? `
            <div style="margin-top: 10px; color: #4b5563;">
              ${taskData.description}
            </div>
            ` : ''}
          </div>
          
          <div class="action-container">
            <div class="action-header">
              <span class="updater">${updaterName}</span>
              <span class="action-date">${currentDate}</span>
            </div>
            <p>
              ${actionType === 'status-update' 
                ? `Task status changed from <strong>${previousStatus}</strong> to <strong>${newStatus}</strong>.` 
                : actionType === 'archive' 
                  ? 'This task has been archived and is no longer active.' 
                  : actionType === 'delete' 
                    ? 'This task has been permanently deleted.' 
                    : 'Task information has been updated.'}
            </p>
          </div>
          
          ${actionType !== 'delete' ? `
          <a href="${process.env.REACT_APP_URL || 'https://revamped-ppc.myelinz.com'}/my-tasks" class="action-button">View Tasks</a>
          ` : ''}
          
          <p class="disclaimer">
            Please do not reply to this email. This message was automatically generated by the MyelinZ PPC system.
          </p>
        </div>
        
        <div class="email-footer">
          <p>&copy; ${new Date().getFullYear()} MyelinZ PPC. All rights reserved.</p>
        </div>
      </div>
    </body>
    </html>
  `;
};

// Helper functions
const formatDate = (dateValue) => {
  if (!dateValue) return '';
  
  let date;
  if (dateValue.seconds) {
    // Firestore timestamp
    date = new Date(dateValue.seconds * 1000);
  } else if (dateValue instanceof Date) {
    date = dateValue;
  } else {
    // Try to parse string or number
    date = new Date(dateValue);
  }
  
  if (isNaN(date.getTime())) return '';
  
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const getPriorityColor = (priority) => {
  switch (priority?.toLowerCase()) {
    case 'high':
      return { bg: '#fee2e2', text: '#b91c1c' };
    case 'medium':
      return { bg: '#fef3c7', text: '#b45309' };
    case 'low':
      return { bg: '#e0f2fe', text: '#0369a1' };
    default:
      return { bg: '#f3f4f6', text: '#4b5563' };
  }
};

export default generateTaskStatusUpdateEmailHTML; 